import { useEffect, useState } from 'react';

import { SERVER_ORIGIN } from '../config';
import { useDialog } from './Dialog';

// Account control surface: shows what we have on file (username, email, join
// date, accepted Terms version) and exposes the two GDPR-mandated controls:
// data export (art. 20) and account deletion (art. 17).

interface AccountInfo {
  id: string;
  username: string;
  email: string | null;
  emailVerified: boolean;
  avatarId: number | null;
  createdAt?: string;
  acceptedTermsVersion: string | null;
  termsCurrentVersion: string;
  settings?: {
    runTutorialSeen?: boolean;
  };
}

export function AccountSettings({
  token,
  user,
  onUpdateSettings,
  onAccountDeleted,
}: {
  token: string;
  user: {
    id: string;
    username: string;
    avatarId: number | null;
    email: string | null;
    emailVerified: boolean;
    acceptedTermsVersion?: string | null;
    bankedResources?: Record<string, number>;
    stats?: {
      matchesPlayed: number;
      matchesWon: number;
      matchesLost: number;
      matchesDrawn: number;
    };
    settings?: {
      runTutorialSeen?: boolean;
    };
  };
  onUpdateSettings: (settings: { runTutorialSeen?: boolean }) => Promise<void>;
  onAccountDeleted: () => void;
}) {
  const dialog = useDialog();
  const [info, setInfo] = useState<AccountInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${SERVER_ORIGIN}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          setLoadError('Failed to load account info.');
          return;
        }
        setInfo(await res.json());
      } catch {
        setLoadError('Network error loading account info.');
      }
    })();
  }, [token]);

  // Download the export as a JSON file. We do a regular fetch (so the bearer
  // token is sent), then turn the response into a blob and synthesise an
  // <a download> click. Window.open would lose the Authorization header.
  const exportData = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${SERVER_ORIGIN}/auth/me/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        await dialog.alert({ title: 'Export failed', message: 'Could not export your data.', variant: 'error' });
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `grandsnatch-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const deleteAccount = async () => {
    const password = window.prompt(
      'WARNING: this will permanently delete your account and ALL your cards, decks and runs.\n\n' +
      'This action cannot be undone.\n\nType your password to confirm:'
    );
    // window.prompt is deliberate here — useDialog().confirm doesn't take an
    // input field, and writing a full custom modal for one one-shot action
    // is overkill. The destructive flow already has the password re-confirm
    // gate server-side.
    if (!password) return;

    try {
      const res = await fetch(`${SERVER_ORIGIN}/auth/me`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ password }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) {
        await dialog.alert({
          title: 'Deletion failed',
          message: j.error ?? 'Could not delete your account.',
          variant: 'error',
        });
        return;
      }
      // Success — kick out of the app and let App.tsx route to /login.
      onAccountDeleted();
    } catch {
      await dialog.alert({ title: 'Network error', message: 'Try again later.', variant: 'error' });
    }
  };

  if (loadError) return <div style={{ color: '#e57373', padding: 24 }}>{loadError}</div>;
  if (!info) return <div style={{ color: '#888', padding: 24 }}>Loading...</div>;

  const runTutorialSeen = user.settings?.runTutorialSeen ?? info.settings?.runTutorialSeen ?? false;

  return (
    <div style={{
      padding: '24px 32px', maxWidth: 720, margin: '0 auto', overflowY: 'auto',
      height: '100%', boxSizing: 'border-box',
    }}>
      <h2 style={{ color: 'var(--accent-color)', fontSize: 22, margin: '0 0 18px' }}>Account</h2>

      <Section title="Profile">
        <Row label="Username" value={info.username} />
        <Row
          label="Email"
          value={info.email ?? '—'}
          badge={info.email ? (info.emailVerified ? { text: 'verified', color: '#4CAF50' } : { text: 'unverified', color: '#ffb703' }) : undefined}
        />
        <Row label="Member since" value={info.createdAt ? new Date(info.createdAt).toLocaleDateString() : '—'} />
        <Row label="Accepted Terms version" value={info.acceptedTermsVersion ?? '—'} />
        {info.acceptedTermsVersion && info.acceptedTermsVersion !== info.termsCurrentVersion && (
          <div style={{ color: '#ffb703', fontSize: 11, marginTop: 6 }}>
            A new version ({info.termsCurrentVersion}) of the Terms is available. We'll ask you to re-accept on your next login.
          </div>
        )}
      </Section>

      <Section title="Security">
        <p style={{ color: '#aaa', fontSize: 12, margin: '0 0 14px', lineHeight: 1.5 }}>
          Change your password. If you've forgotten it, sign out and use the "Forgot password?" link on the sign-in page.
        </p>
        <button onClick={() => setChangePwOpen(true)} style={btnStyle('#4fc3f7')}>
          🔒 Change password
        </button>
      </Section>

      {changePwOpen && (
        <ChangePasswordModal
          token={token}
          onClose={() => setChangePwOpen(false)}
        />
      )}

      <Section title="Preferences">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
          <div>
            <p style={{ color: '#eee', fontSize: 13, fontWeight: 'bold', margin: 0 }}>Map Tutorial Guide</p>
            <p style={{ color: '#aaa', fontSize: 12, margin: '4px 0 0 0', lineHeight: 1.4 }}>
              {runTutorialSeen
                ? 'The introductory guide for the run map is currently hidden.'
                : 'The introductory guide will appear the next time you open the run map.'}
            </p>
          </div>
          <button
            onClick={async () => {
              await onUpdateSettings({ runTutorialSeen: false });
              if (info) {
                setInfo({
                  ...info,
                  settings: { ...info.settings, runTutorialSeen: false }
                });
              }
              await dialog.alert({
                title: 'Guide Re-enabled',
                message: 'The map tutorial guide will be shown the next time you open the run map.',
                variant: 'info'
              });
            }}
            disabled={!runTutorialSeen}
            style={btnStyle(runTutorialSeen ? '#4fc3f7' : '#555')}
          >
            {runTutorialSeen ? '🔄 Re-enable Guide' : '✓ Guide Active'}
          </button>
        </div>
      </Section>

      <Section title="Data (GDPR)">
        <p style={{ color: '#aaa', fontSize: 12, margin: '0 0 14px', lineHeight: 1.5 }}>
          You have the right to a copy of your data (art. 20 GDPR) and to delete your account (art. 17 GDPR).
          See the <a href="/legal/privacy" style={{ color: '#4fc3f7' }}>Privacy Policy</a> for details.
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button
            onClick={exportData}
            disabled={exporting}
            style={btnStyle('#4fc3f7')}
          >{exporting ? 'Exporting...' : '⬇ Export my data'}</button>
          <button
            onClick={deleteAccount}
            style={btnStyle('#e57373')}
          >🗑 Delete my account</button>
        </div>
      </Section>

      <Section title="Legal">
        <div style={{ display: 'flex', gap: 16, fontSize: 12 }}>
          <a href="/legal/privacy" style={{ color: '#4fc3f7' }}>Privacy Policy</a>
          <a href="/legal/terms" style={{ color: '#4fc3f7' }}>Terms of Service</a>
          <a href="/legal/cookies" style={{ color: '#4fc3f7' }}>Cookie Policy</a>
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{
      marginBottom: 24, padding: 18, background: '#15151f',
      border: '1px solid #2a2a3a', borderRadius: 6,
    }}>
      <h3 style={{ margin: '0 0 12px', color: '#ddd', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1 }}>{title}</h3>
      {children}
    </div>
  );
}

function Row({
  label, value, badge,
}: {
  label: string; value: string; badge?: { text: string; color: string };
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #1f1f2a' }}>
      <span style={{ color: '#888', fontSize: 12 }}>{label}</span>
      <span style={{ color: '#eee', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
        {value}
        {badge && (
          <span style={{
            fontSize: 10, fontWeight: 'bold', color: '#000',
            background: badge.color, padding: '1px 8px', borderRadius: 3,
            letterSpacing: 0.4,
          }}>{badge.text}</span>
        )}
      </span>
    </div>
  );
}

function btnStyle(color: string): React.CSSProperties {
  return {
    background: 'transparent', color, border: `1.5px solid ${color}`,
    padding: '8px 16px', borderRadius: 4, cursor: 'pointer',
    fontFamily: 'inherit', fontSize: 13, fontWeight: 'bold',
  };
}

// Themed alternative to a stack of window.prompt() calls. Asks for the
// current password (anti-stolen-token), a new password and its confirm.
// Stays open on error so the user can correct without re-typing.
function ChangePasswordModal({
  token, onClose,
}: {
  token: string; onClose: () => void;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (next.length < 6) { setError('New password must be at least 6 characters.'); return; }
    if (next !== confirm) { setError('Passwords do not match.'); return; }
    if (next === current) { setError('New password must differ from the current one.'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${SERVER_ORIGIN}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPassword: current, newPassword: next }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(j.error ?? 'Password change failed.');
        return;
      }
      setDone(true);
      setTimeout(onClose, 1400);
    } catch {
      setError('Network error.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#0c0c16', border: '2px solid var(--accent-color)',
          borderRadius: 10, padding: 22, width: 360, maxWidth: '92vw',
          color: 'var(--text-color)',
        }}
      >
        <h2 style={{ margin: '0 0 14px', color: 'var(--accent-color)', fontSize: 18 }}>Change password</h2>
        {done ? (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <div style={{ fontSize: 32 }}>✓</div>
            <div style={{ color: '#4CAF50', fontWeight: 'bold', marginTop: 4 }}>Password updated.</div>
          </div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input
              type="password"
              placeholder="current password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              autoFocus
              disabled={busy}
              style={pwInputStyle}
            />
            <input
              type="password"
              placeholder="new password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              disabled={busy}
              style={pwInputStyle}
            />
            <input
              type="password"
              placeholder="confirm new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              disabled={busy}
              style={pwInputStyle}
            />
            {error && <div style={{ color: '#e57373', fontSize: 12 }}>{error}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                style={{
                  background: 'none', border: '1px solid #555', color: '#ccc',
                  padding: '6px 14px', borderRadius: 4, cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 12,
                }}
              >Cancel</button>
              <button
                type="submit"
                disabled={busy || !current || !next || !confirm}
                className="multi-btn"
                style={{ fontSize: 12 }}
              >{busy ? '...' : 'Update password'}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const pwInputStyle: React.CSSProperties = {
  padding: '9px 12px',
  fontSize: 13,
  fontFamily: 'inherit',
  background: '#1a1a24',
  border: '1px solid #444',
  borderRadius: 4,
  color: 'var(--text-color)',
};
